import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, ExternalLink, LoaderCircle, RefreshCcw, ShieldCheck } from "lucide-react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChatPanel } from "@/components/ChatPanel";
import { useWallet } from "@/context/WalletContext";
import {
  baseScanTokenUrl,
  marketplaceTokenUrl,
  readOnchainAvatar,
  updateOnchainPersonality,
  type OnchainAvatar,
} from "@/lib/contract";
import { parsePersonality, personalityJson } from "@/lib/personality";
import type { PersonalityCard } from "@/types";

export function AvatarPage() {
  const { tokenId: tokenParam } = useParams();
  const tokenId = useMemo(() => {
    try { return BigInt(tokenParam ?? "0"); } catch { return 0n; }
  }, [tokenParam]);
  const { address, walletClient } = useWallet();
  const [avatar, setAvatar] = useState<OnchainAvatar | null>(null);
  const [personality, setPersonality] = useState<PersonalityCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await readOnchainAvatar(tokenId);
      setAvatar(next);
      setPersonality(parsePersonality(next.personalityJson));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not read this NFT.");
    } finally {
      setLoading(false);
    }
  }, [tokenId]);

  useEffect(() => { void load(); }, [load]);
  const isOwner = Boolean(address && avatar && address.toLowerCase() === avatar.owner.toLowerCase());

  const publish = async () => {
    if (!address || !walletClient || !personality) return;
    setPublishing(true);
    setError(null);
    try {
      await updateOnchainPersonality(walletClient, address, tokenId, personalityJson(personality));
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not publish the personality.");
    } finally {
      setPublishing(false);
    }
  };

  if (loading) return <main className="grid min-h-[70vh] place-items-center"><div className="text-center"><LoaderCircle className="mx-auto size-7 animate-spin text-cyan-300" /><p className="mt-3 text-sm text-white/45">Reading Base Sepolia</p></div></main>;
  if (!avatar) return <main className="mx-auto max-w-xl px-5 py-24 text-center"><h1 className="text-3xl font-black">Avatar not found</h1><p className="mt-3 text-white/45">{error ?? "This token does not exist or the contract is not configured."}</p></main>;

  return (
    <main className="mx-auto max-w-[1320px] px-4 py-10 sm:px-6">
      <div className="grid gap-8 lg:grid-cols-[.88fr_1.12fr]">
        <div>
          <div className="overflow-hidden rounded-[30px] border border-white/10 bg-black shadow-2xl"><img alt={avatar.metadata.name} className="aspect-square w-full" src={avatar.metadata.image} /></div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button className="border-white/10 bg-white/5" onClick={() => void navigator.clipboard.writeText(window.location.href)} variant="outline"><Copy className="size-4" />Copy page</Button>
            <Button asChild className="border-white/10 bg-white/5" variant="outline"><a href={baseScanTokenUrl(tokenId)} rel="noreferrer" target="_blank">BaseScan <ExternalLink className="size-4" /></a></Button>
            <Button asChild className="border-white/10 bg-white/5" variant="outline"><a href={marketplaceTokenUrl(tokenId)} rel="noreferrer" target="_blank">Marketplace <ExternalLink className="size-4" /></a></Button>
          </div>
        </div>
        <div>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div><p className="text-[11px] font-bold tracking-[.22em] text-cyan-200/65">ANISOUL #{tokenId.toString()}</p><h1 className="mt-2 text-4xl font-black tracking-tight">{personality?.name || avatar.metadata.name}</h1><p className="mt-2 max-w-xl text-white/48">{personality?.lore || avatar.metadata.description}</p></div>
            <span className="rounded-full border border-cyan-300/20 bg-cyan-300/7 px-3 py-1.5 text-xs font-bold text-cyan-100">{avatar.rarity}</span>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {avatar.metadata.attributes.slice(0, 8).map((attribute) => <div className="rounded-2xl border border-white/8 bg-white/[.03] p-3" key={attribute.trait_type}><p className="text-[10px] uppercase tracking-wider text-white/30">{attribute.trait_type}</p><p className="mt-1 truncate text-sm font-semibold">{attribute.value}</p></div>)}
          </div>
          <div className="mt-6 flex items-center justify-between gap-3 rounded-2xl border border-white/8 bg-white/[.025] p-4"><div><p className="text-xs text-white/35">Current owner</p><p className="mt-1 break-all font-mono text-xs text-white/65">{avatar.owner}</p></div>{isOwner && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-300/8 px-2.5 py-1 text-[10px] font-bold text-emerald-200"><ShieldCheck className="size-3" />YOU OWN THIS</span>}</div>
          {!avatar.revealed && <div className="mt-4 flex items-center gap-3 rounded-2xl border border-amber-300/15 bg-amber-300/6 p-4 text-sm text-amber-100/75"><RefreshCcw className="size-4 animate-spin" />Pyth Entropy reveal is pending. Refresh this page after the callback.</div>}
          {error && <p className="mt-4 text-sm text-red-300">{error}</p>}
        </div>
      </div>

      <section className="mt-12 border-t border-white/8 pt-10">
        <div className="mb-5 flex items-end justify-between gap-4"><div><p className="text-[11px] font-bold tracking-[.2em] text-pink-300/70">LIVE PERSONALITY</p><h2 className="mt-1 text-2xl font-black">Talk with {personality?.name || "this AniSoul"}</h2></div>{isOwner && personality && <Button className="bg-cyan-300 text-[#06100f] hover:bg-cyan-200" disabled={publishing} onClick={() => void publish()}>{publishing ? <LoaderCircle className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}{publishing ? "Publishing" : "Publish evolution"}</Button>}</div>
        <ChatPanel onPersonality={setPersonality} personality={personality} sessionId={`token-${tokenId}`} traits={avatar.traits} />
        {!isOwner && <p className="mt-3 text-center text-xs text-white/35">Your conversation stays local and cannot change the public personality. Only the current owner can publish an evolution.</p>}
      </section>
    </main>
  );
}
